import React, { useEffect, useRef } from 'react';

import LineItem from '../../../../shared/components/LineItem';
import OrderSummary from '../../../../shared/components/OrderSummary';
import { MINI_BASKET_CONSTANTS } from '../../constants/miniBasketConstants';
import { isPassProduct } from '../../utils/cartPageHelpers';

import { MiniBasketDisplayItem } from './miniBasketTypes';

export interface MiniBasketDropdownProps {
    open: boolean;
    isOpen?: boolean; // propItems/external control
    items: MiniBasketDisplayItem[];
    groups: Record<string, MiniBasketDisplayItem[]>;
    looseItems: MiniBasketDisplayItem[];
    groupedCount: number;
    updatingItems: Set<string>;
    eventKey?: string;
    onClose: () => void;
    handleDelete: (id: string) => Promise<void>;
    handleIncrement: (id: string) => Promise<void>;
    handleDecrement: (id: string) => Promise<void>;
    handleGroupDelete: (items: MiniBasketDisplayItem[]) => Promise<void>;
    handleGroupIncrement: (items: MiniBasketDisplayItem[]) => Promise<void>;
    handleGroupDecrement: (items: MiniBasketDisplayItem[]) => Promise<void>;
}

const MiniBasketDropdown: React.FC<MiniBasketDropdownProps> = ({
    open,
    isOpen,
    items,
    groups,
    looseItems,
    groupedCount,
    updatingItems,
    eventKey,
    onClose,
    handleDelete,
    handleIncrement,
    handleDecrement,
    handleGroupDelete,
    handleGroupIncrement,
    handleGroupDecrement,
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Prevent background scroll when mini basket is open
    useEffect(() => {
        const el = dropdownRef.current;
        if (!el || (!open && !isOpen)) return;

        const handleWheel = (e: WheelEvent) => {
            const scrollable = el.querySelector('.-basket-scrollable');
            if (scrollable && scrollable.contains(e.target as Node)) {
                e.stopPropagation();
                const { scrollTop, scrollHeight, clientHeight } = scrollable;
                const isAtTop = scrollTop === 0;
                const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
                if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
                    e.preventDefault();
                }
                return;
            }
            e.preventDefault();
            e.stopPropagation();
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [open, isOpen]);

    if (!open && !isOpen) return null;

    return (
        <div
            ref={dropdownRef}
            className="hidden md:flex md:flex-col absolute -right-[17px] top-full mt-3 w-[350px] bg-white rounded-[12px] shadow-xl ring-1 ring-gray-200 z-50 text-black"
        >

            {/* ── Section A: Header (48px, bg-white) ── */}
            <div className="flex-shrink-0 w-[350px] h-[48px] flex items-center gap-[4px] px-[12px] rounded-tl-[12px] rounded-tr-[12px] box-border bg-white">
                {/* "Basket" – 46×24, #002B66, weight 500, size 14 */}
                <span
                    className="whitespace-nowrap"
                    style={{ display: 'inline-block', width: 46, height: 24, color: '#002B66', fontWeight: 500, fontSize: 14, lineHeight: '24px' }}
                >
                    {MINI_BASKET_CONSTANTS.TITLE}
                </span>
                {/* "[X] Item[s]" – 43×18, #002B66@70%, weight 400, size 12 */}
                <span
                    className="whitespace-nowrap"
                    style={{ display: 'inline-block', width: 43, height: 18, color: 'rgba(0,43,102,0.7)', fontWeight: 400, fontSize: 12, lineHeight: '18px', marginTop: 1 }}
                >
                    {groupedCount} {groupedCount === 1 ? 'Item' : 'Items'}
                </span>
            </div>

            {/* ── Section B: Scrollable items (bg #F9FAFB, max-h 350px) ── */}
            <div
                className="-basket-scrollable w-[350px] flex-1 overflow-y-auto py-[12px] pl-[12px] pr-0 box-border"
                style={{ maxHeight: 350, backgroundColor: '#F9FAFB' }}
            >
                {items.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                        <p className="text-gray-500 text-sm mb-0">{MINI_BASKET_CONSTANTS.EMPTY_MESSAGE}</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-[8px]">
                        {/* Groups organised by delegate rate */}
                        {(() => {
                            const byRate: Record<string, Array<{ key: string; items: MiniBasketDisplayItem[] }>> = {};
                            Object.entries(groups).forEach(([key, groupItems]) => {
                                const mainItem =
                                    groupItems.find(
                                        (i) =>
                                            ((i.custom?.fields?.['selectedOptionsList'] as string[] | undefined)?.length ?? 0) > 0
                                    ) || groupItems[0];
                                const rate = mainItem.custom?.fields?.['delegateRate'] as string | undefined;
                                const rateKey = rate || 'Other';
                                if (!byRate[rateKey]) byRate[rateKey] = [];
                                byRate[rateKey].push({ key, items: groupItems });
                            });

                            return Object.entries(byRate).map(([_rateName, rateGroups]) => (
                                <div key={_rateName} className="flex flex-col gap-[8px]">

                                    {rateGroups.map(({ key, items: groupItems }) => {
                                        const effectiveMainItems = groupItems.filter((i) => isPassProduct(i));
                                        const effectiveMainItem = effectiveMainItems[0] || groupItems[0];
                                        const totalQuantity =
                                            effectiveMainItems.reduce((sum, i) => sum + i.quantity, 0) ||
                                            effectiveMainItem.quantity;

                                        const mainIds = new Set(effectiveMainItems.map((i) => i.id));
                                        const addOnItems = groupItems.filter((i) => !mainIds.has(i.id));

                                        const { custom: _custom, ...restItems } = effectiveMainItem;
                                        const compositeItem = {
                                            ...restItems,
                                            quantity: totalQuantity,
                                            startDate:
                                                (effectiveMainItem.variant?.attributes?.find(
                                                    (a: { name: string; value: unknown }) => a.name === 'startDate'
                                                )?.value as string | undefined) ||
                                                (effectiveMainItem.custom?.fields?.startDate as string | undefined) ||
                                                effectiveMainItem.startDate,
                                            endDate:
                                                (effectiveMainItem.variant?.attributes?.find(
                                                    (a: { name: string; value: unknown }) => a.name === 'endDate'
                                                )?.value as string | undefined) ||
                                                (effectiveMainItem.custom?.fields?.endDate as string | undefined) ||
                                                effectiveMainItem.endDate,
                                            totalPrice: {
                                                centAmount: groupItems.reduce(
                                                    (sum, i) => sum + (i.totalPrice?.centAmount || 0),
                                                    0
                                                ),
                                                currencyCode: effectiveMainItem.totalPrice?.currencyCode || 'USD',
                                            },
                                            addOns: [
                                                ...new Set(
                                                    addOnItems.map((i) => {
                                                        if (!i.name) return 'Add-on';
                                                        if (typeof i.name === 'string') return i.name;
                                                        const nameObj = i.name as Record<string, string>;
                                                        if (nameObj['en']) return nameObj['en'];
                                                        const currentLoc = sessionStorage.getItem('locale');
                                                        if (currentLoc && nameObj[currentLoc]) return nameObj[currentLoc];
                                                        const values = Object.values(nameObj);
                                                        return values.length > 0 ? values[0] : 'Add-on';
                                                    })
                                                ),
                                            ].sort(),
                                        };

                                        const firstItem = groupItems[0];
                                        const assocKey = firstItem.custom?.fields?.['associationKey'] as string | undefined;
                                        const delegateRate = (firstItem.custom?.fields?.['delegateRate'] as string | undefined) || '';
                                        const groupKey =
                                            assocKey && delegateRate
                                                ? `${assocKey}||${delegateRate}`
                                                : assocKey || groupItems.map((i) => i.id).sort().join('_');
                                        const isUpdating = updatingItems.has(groupKey);

                                        // Merge delegateRate so LineItem can show it as attendee type
                                        const compositeItemWithRate = { ...compositeItem, delegateRate };

                                        return (
                                            <div
                                                key={`group-${key}`}
                                                className="border border-[#DFE4EC] rounded-[4px] p-[12px] bg-white w-[326px] min-h-[102px]"
                                            >
                                                <LineItem
                                                    item={compositeItemWithRate}
                                                    currencyCode={effectiveMainItem.currencyCode || 'USD'}
                                                    locale={effectiveMainItem.locale || 'en'}
                                                    onDelete={() => handleGroupDelete(groupItems)}
                                                    onIncrement={() => handleGroupIncrement(groupItems)}
                                                    onDecrement={() => handleGroupDecrement(groupItems)}
                                                    isUpdating={isUpdating}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            ));
                        })()}

                        {/* Loose items (not part of any group) */}
                        {looseItems.map((item) => {
                            const { custom: _custom, ...restItem } = item;
                            return (
                                <div
                                    key={item.id}
                                    className="border border-[#DFE4EC] rounded-[4px] p-[12px] bg-white w-[326px] min-h-[102px]"
                                >
                                    <LineItem
                                        item={restItem}
                                        currencyCode={item.currencyCode || 'USD'}
                                        locale={item.locale || 'en'}
                                        onDelete={handleDelete}
                                        onIncrement={handleIncrement}
                                        onDecrement={handleDecrement}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ── Section C: Footer / Order Summary (min-h 123px) ── */}
            <div className="flex-shrink-0 w-[350px] border-t border-gray-200 px-[12px] py-[12px] min-h-[123px] box-border">
                <OrderSummary
                    items={items}
                    eventKey={eventKey}
                    onAction={onClose}
                />
            </div>
        </div>
    );
};

export default MiniBasketDropdown;
